/**
 * ConnectionAvatar — circular avatar for connection detail page headers.
 *
 * Consolidates the inline avatar markup that was duplicated across the three
 * detail pages (Profile, Anon, Manual). This is a pure consolidation: each page
 * passes its own placeholder colors and no-name fallback, so the rendered avatar
 * is byte-for-byte unchanged. The one intentional new behavior is the Royale
 * ring (`isRoyaleUser`) — previously only on People list cards — matching the
 * Claude Design mockups, which show the ring on all detail page types for Royale
 * users.
 *
 * Render priority inside the circle: photo → name initial → fallbackIcon → empty.
 *
 * @param {string} photoUrl - Avatar photo; shown as a cover background image when present
 * @param {string} name - Display name; its first letter is the placeholder when there's no photo
 * @param {boolean} isRoyaleUser - Adds the gray Royale ring (#999, 2px) when true
 * @param {number} size - Avatar diameter in px (default 90)
 * @param {string} placeholderBg - Background behind the initial when there's no photo
 * @param {string} initialColor - Color of the placeholder initial letter
 * @param {React.ReactNode} fallbackIcon - Rendered when there's no photo and no name
 */
const ConnectionAvatar = ({
  photoUrl,
  name,
  isRoyaleUser = false,
  size = 90,
  placeholderBg = '#C7C7CC',
  initialColor = '#FFFFFF',
  fallbackIcon = null,
}) => {
  const initial = name ? name.charAt(0).toUpperCase() : null;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        padding: isRoyaleUser ? '2px' : '0',
        border: isRoyaleUser ? '2px solid #999' : 'none',
        backgroundColor: isRoyaleUser ? '#FFFFFF' : 'transparent',
      }}
    >
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          backgroundColor: photoUrl ? 'transparent' : placeholderBg,
          backgroundImage: photoUrl ? `url(${photoUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '36px',
          fontWeight: '600',
          color: initialColor,
        }}
      >
        {!photoUrl && (initial || fallbackIcon)}
      </div>
    </div>
  );
};

export default ConnectionAvatar;
